import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import { getAddressByPostalCode } from 'modules/clients/actions';
import Select from 'components/UI/Select';
import { translate } from 'utils/translate';
import StepHeader from './StepHeader';

const FIELDS = ['country', 'postalCode', 'state', 'city', 'neighborhood', 'street', 'number'];

const ClientAddress = ({
  data,
  literals,
  onContinue,
  onBack,
  onEvent,
  onError,
  headerStep,
  headerTotal,
  fieldErrors = {},
}) => {
  const [address, setAddress] = useState({
    country: data.address?.country || 'MÉXICO',
    state: '',
    city: '',
    postalCode: data.address?.postalCode || '',
    neighborhood: '',
    street: '',
    number: '',
    internalNumber: '', // optional
  });
  const [validated, setValidated] = useState(false);
  const [options, setOptions] = useState({ data: { states: [], cities: [] } });
  const dispatch = useDispatch();

  const neighborhoods = useMemo(() => {
    if (!options || !address?.city) return [];
    const city = options.data.cities.find((c) => c.value === address.city);
    return city?.colonies || [];
  }, [options, address]);

  const canContinue = useMemo(() => {
    return FIELDS.every((key) => address?.[key]);
  }, [address]);

  const handleContinue = () => {
    onContinue(address);
  };

  const handleValidate = (e) => {
    if (e) {
      e.preventDefault();
    }
    setValidated(false);
    setAddress({
      ...address,
      state: '',
      city: '',
      neighborhood: '',
    });
    dispatch(getAddressByPostalCode(address.postalCode)).then((response) => {
      if (response?.states?.length > 0) {
        setOptions((prev) => ({ ...prev, data: response }));
        const auxAddress = {};
        if (response.states.length === 1) {
          auxAddress.state = response.states[0].value;
        }
        if (response.cities.length === 1) {
          auxAddress.city = response.cities[0].value;
          if (response.cities[0].colonies.length === 1) {
            auxAddress.neighborhood = response.cities[0].colonies[0].value;
          }
        }
        if (Object.keys(auxAddress).length > 0) {
          setAddress((prev) => ({ ...prev, ...auxAddress }));
        }
        setValidated(true);
      } else {
        onError(translate(literals.address.postalCodeNotFound, { postalCode: address.postalCode }));
      }
    });
  };

  useEffect(() => {
    if (address.postalCode?.length === 5) {
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.postalCode]);

  const handleFormatPostalCode = () => {
    let auxPostalCode = address.postalCode.replace(/\D/g, '');
    if (auxPostalCode.length > 5) {
      auxPostalCode = auxPostalCode.slice(0, 5);
    }
    if (auxPostalCode !== address.postalCode) {
      setAddress({ ...address, postalCode: auxPostalCode });
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleNumberChange = (value, field) => {
    setAddress((prev) => ({
      ...prev,
      [field]: (value || '').toString().replace(/[^0-9]/g, ''),
    }));
  };

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={headerStep} total={headerTotal} onEvent={onEvent} />
        <p className='font-bold text-primary mb-6 text-xl'>{literals.address.validateAddress}</p>
        <p>{literals.address.writeYourPostalCode}</p>
        <Input
          className='mb-2'
          label={literals.address.country}
          placeholder={literals.address.country}
          value={address.country}
          error={fieldErrors.country || ''}
          disabled
        />
        <form onSubmit={handleValidate}>
          <Input
            type='number'
            className='mb-2'
            label={literals.address.postalCode}
            placeholder={literals.address.postalCode}
            value={address.postalCode}
            onChange={(postalCode) => setAddress({ ...address, postalCode })}
            error={fieldErrors.postalCode || (address.postalCode.length >= 1 && address.postalCode.length < 5 ? literals.address.errorPostalCodeLength : false)}
            onBlur={handleFormatPostalCode}
            required
          />
        </form>
        <div className={`mb-6 ${!validated ? 'opacity-50' : ''}`}>
          <Select
            className='mb-2'
            label={literals.address.state}
            placeholder={literals.address.state}
            options={options.data.states}
            value={address.state}
            error={fieldErrors.state || ''}
            onChange={(state) => setAddress({ ...address, state })}
            disabled
            required
          />
          <Select
            className='mb-2'
            label={literals.address.city}
            placeholder={literals.address.city}
            options={options.data.cities}
            value={address.city}
            error={fieldErrors.city || ''}
            onChange={(city) => setAddress({ ...address, city })}
            disabled={options.data.cities.length <= 1}
            required
          />
          <Select
            className='mb-2'
            label={literals.address.neighborhood}
            placeholder={literals.address.neighborhood}
            options={neighborhoods}
            value={address.neighborhood}
            error={fieldErrors.neighborhood || ''}
            onChange={(neighborhood) => setAddress({ ...address, neighborhood })}
            disabled={!validated}
            required
          />
          <Input
            className='mb-2'
            label={literals.address.street}
            placeholder={literals.address.street}
            value={address.street}
            error={fieldErrors.street || ''}
            onChange={(street) => setAddress({ ...address, street: street ? street.toUpperCase() : '' })}
            disabled={!validated}
            required
          />
          <Input
            className='mb-2'
            inputMode='numeric'
            label={literals.address.number}
            placeholder={literals.address.number}
            value={address.number}
            error={fieldErrors.number || ''}
            onChange={(number) => handleNumberChange(number, 'number')}
            onKeyPress={handleKeyPress}
            disabled={!validated}
            required
          />
          <Input
            className='mb-6'
            inputMode='numeric'
            label={literals.address.internalNumber}
            placeholder={literals.address.internalNumber}
            value={address.internalNumber}
            error={fieldErrors.internalNumber || ''}
            onChange={(internalNumber) => handleNumberChange(internalNumber, 'internalNumber')}
            onKeyPress={handleKeyPress}
            disabled={!validated}
          />
        </div>
      </div>
      <div className='screen-actions'>
        <Button onClick={handleContinue} disabled={!canContinue}>{literals.common.next}</Button>
        <Button type='outline-primary' onClick={onBack}>{literals.common.back}</Button>
      </div>
    </div>
  );
};

ClientAddress.propTypes = {
  data: PropTypes.object.isRequired,
  literals: PropTypes.object.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  headerStep: PropTypes.number.isRequired,
  headerTotal: PropTypes.number.isRequired,
  fieldErrors: PropTypes.object,
};

export default ClientAddress;
